import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import Heading from '../../../../generic-components/Heading';
// import Button from '../../../../generic-components/Button';
import { CreationContext } from '../InvoiceCreation';
import { MAX_INVOICE_AMOUNT, STYLES, TEXTS } from '../consts/consts';
import { UserMessage } from '../../../../DesignSystem/LiliUserMessage/UserMessage';
import { SliderUserMessage } from './SliderUserMessage';
import { IInvoiceReminder, IProduct } from '../types/types';
import { useCreateInvoice } from '../hooks/useCreateInvoice';
import { useStore } from '../../../../store';
import { VIEWS_MAP } from '../../../../router/constants';
import { useUpdateExistingInvoice } from '../hooks/useUpdateExistingInvoice';
import { useCreateNewServiceItem } from '../hooks/useCreateNewServiceItem';
import { GenericErrorModal } from '../../../../components/errors/GenericErrorModal';
import { ReactComponent as EyeIcon } from '../../../../assets/icons/svg/shared/ic-shared-eye-opened.svg';
import Spinner, { SpinnerColor } from '../../../../generic-components/Spinner';
import { useAccountSummaryQuery } from '../../../../hooks/query/useAccountSummaryQuery';
import { formatValueWithoutCurrency } from '../../../../helpers/currency.helper';

const {
  INVOICE_CREATION: { STICKY_HEADER: componentStyles },
} = STYLES;

const {
  INVOICE_CREATION: { STICKY_HEADER: componentTexts },
} = TEXTS;

export const StickyHeader = () => {
  const actionFlags = useStore((state) => state.summary.actionFlags);
  const [isInvoicePreviewShown, setIsInvoicePreviewShown] = useState<boolean>(false);
  const [shouldShowCopy, setShouldShowCopy] = useState<boolean>(false);
  const contextData = useContext(CreationContext);
  const invoiceData = useStore((state) => state.invoiceData);
  const localInvoiceServiceItems = useStore((state) => state.localInvoiceServiceItems);
  const setIsNeedToValidateItems = useStore((state) => state.setIsNeedToValidateItems);
  const setTemporaryItemsBeingSaved = useStore((state) => state.setTemporaryItemsBeingSaved);
  const setInvoiceItemsToUpdate = useStore((state) => state.setInvoiceItemsToUpdate);

  const itemsBeingSavedRef = useRef<any>({});
  const localItemsArrayRef = useRef<any>([]);
  const clickedButtonRef = useRef<string>('');

  const invoiceRemindersData = useStore((state) => state.invoiceRemindersData);
  const {
    customer: { externalId },
  } = useStore((state) => state.summary);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const totalPrice = () =>
    localInvoiceServiceItems.reduce((sum, product) => sum + product.amount * product.quantity, 0);
  const hasInvoiceRecipient = useMemo(() => !!invoiceData?.invoiceRecipient?.name, [invoiceData]);
  const isInvoiceAmountValid = useMemo(
    () => totalPrice() > 0 && totalPrice() < MAX_INVOICE_AMOUNT,
    [localInvoiceServiceItems],
  );
  const isItemsValid = useMemo(
    () => localInvoiceServiceItems.every((item) => item.name.length > 0 && localInvoiceServiceItems.length > 0),
    [localInvoiceServiceItems],
  );
  const isCreationFormValid = useMemo(() => hasInvoiceRecipient && isItemsValid && isInvoiceAmountValid, [hasInvoiceRecipient, isItemsValid, isInvoiceAmountValid]);
  const isPreviewAvailable = useMemo(() => hasInvoiceRecipient, [hasInvoiceRecipient]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { refetch: mutateSummary } = useAccountSummaryQuery();
  const { mutateAsync: mutateCreateItem } = useCreateNewServiceItem();
  const { getPersonalAccount } = useStore((state) => state);
  const { accountExternalId } = getPersonalAccount();
  useEffect(() => {
    localItemsArrayRef.current = localInvoiceServiceItems;
  }, [localInvoiceServiceItems]);
  const getActiveReminders = () => {
    const activeEnums: any = [];
    invoiceRemindersData.forEach((i: IInvoiceReminder) => {
      if (i.selected) {
        activeEnums.push(i.invoiceReminderType);
      }
    });
    return activeEnums;
  };
  const { mutateAsync: mutateAsyncCreateItem, isLoading } = useCreateInvoice();
  const triggerCreation = () => {
    mutateAsyncCreateItem({
      customerId: externalId,
      invoiceId: invoiceData?.externalId || '',
      reminderTypes: getActiveReminders(),
    }).then(() => {
      if (!actionFlags.createInvoice) mutateSummary();
      setIsInvoicePreviewShown(false);
      setInvoiceItemsToUpdate([]);
      setIsNeedToValidateItems(false);
      setTimeout(() => {
        if (contextData?.mutateInvoiceCreationStatus) contextData.mutateInvoiceCreationStatus(true);
      }, 100);
    });
  };
  const triggerPreview = () => {
    if (!isPreviewAvailable) return;
    setIsInvoicePreviewShown(true);
  };
  const { mutate: mutateUpdateInvoice, isLoading: isUpdatingInvoice } = useUpdateExistingInvoice(
    () => {
      if (clickedButtonRef.current === 'PREVIEW') {
        triggerPreview();
      } else if (clickedButtonRef.current === 'CREATE') {
        triggerCreation();
      }
    },
    (e) => {
      setErrorMessage(e?.response?.data?.errors?.[0]?.localizedMessage);
    },
  );

  const tipForCreateToolTip = () => {
    if (!hasInvoiceRecipient) {
      return t('INVOICE_CREATION_TOOLTIP_INFO_1');
    }
    if (!isItemsValid) {
      return t('INVOICE_CREATION_TOOLTIP_INFO_2');
    }
    if (!isInvoiceAmountValid) {
      return `${t('INVOICE_CREATION_TOOLTIP_INFO_3')} ${formatValueWithoutCurrency(MAX_INVOICE_AMOUNT, 2)}`;
    }
    return '';
  };

  const attachItemsToInvoice = () => {
    const invoiceDataToUpdate = { ...invoiceData };
    invoiceDataToUpdate.invoiceService.invoiceServiceItems = [...localItemsArrayRef.current];
    mutateUpdateInvoice({
      customerId: externalId,
      invoicePayload: invoiceDataToUpdate,
      invoiceId: invoiceData?.externalId,
    });
  };
  const saveTemporaryItemsToInventory = async () => {
    const itemsNotInInventory = localInvoiceServiceItems.filter((p) => !p.serviceItemId);
    itemsNotInInventory.forEach((p) => {
      itemsBeingSavedRef.current[p.temporaryId] = true;
    });
    setTemporaryItemsBeingSaved(itemsBeingSavedRef.current);
    try {
      const updatePromises = itemsNotInInventory.map((item) => {
        const variables = {
          customerId: externalId,
          accountExternalId,
          payload: {
            name: item.name,
            amount: item.amount,
            description: item.description,
          },
        };

        return mutateCreateItem(variables).then((response) => {
          itemsBeingSavedRef.current[item.temporaryId] = false;
          setTemporaryItemsBeingSaved(itemsBeingSavedRef.current);
          const mappedLocalItems = localItemsArrayRef.current.map((productT: IProduct) => {
            if (productT.temporaryId && productT.temporaryId === item.temporaryId) {
              response.quantity = productT.quantity;
              return response;
            }
            return productT;
          });
          localItemsArrayRef.current = mappedLocalItems;
          setInvoiceItemsToUpdate(mappedLocalItems);
        });
      });
      // Wait for all updates to complete
      await Promise.all(updatePromises);
    } catch (error) {
      setTemporaryItemsBeingSaved({});
    }
  };

  const handleCreateInvoiceClick = async () => {
    clickedButtonRef.current = 'CREATE';
    if (!isItemsValid || localInvoiceServiceItems.length === 0) {
      setIsNeedToValidateItems(true);
      return;
    }
    if (localInvoiceServiceItems.length === 0) {
      attachItemsToInvoice();
    } else {
      saveTemporaryItemsToInventory().then(() => {
        attachItemsToInvoice();
        setIsNeedToValidateItems(false);
      });
    }
  };
  const handlePreviewClick = async () => {
    clickedButtonRef.current = 'PREVIEW';
    if (!isItemsValid) {
      setIsNeedToValidateItems(true);
      return;
    }

    if (localInvoiceServiceItems.length === 0) {
      attachItemsToInvoice();
    } else {
      saveTemporaryItemsToInventory().then(() => {
        attachItemsToInvoice();
        setIsNeedToValidateItems(false);
      });
    }
  };

  const handleClick = (type: string) => {
    if (type === 'CLOSE-ICON') {
      setIsInvoicePreviewShown(false);
      if (contextData.isInvoiceCreated) {
        navigate(`/${VIEWS_MAP.INVOICES}`);
      }
    }
    if (type === 'CLOSE') {
      if (contextData.isInvoiceCreated) {
        setShouldShowCopy(false);
      }
      setIsInvoicePreviewShown(false);
    }
    if (type === 'CREATE_INVOICE') {
      handleCreateInvoiceClick();
    }
  };
  useEffect(() => {
    if (!contextData.isInvoiceCreated) return;
    window.addEventListener('message', (payload) => {
      if (payload.data === 'READY') {
        setTimeout(() => {
          setShouldShowCopy(true);
        }, 0);
      }
    });
    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('message', () => {});
    };
  }, [contextData.isInvoiceCreated]);
  const errorModal = errorMessage && (
    <UserMessage onClick={() => setErrorMessage(null)} id=''>
      <GenericErrorModal
        title='An error has occurred'
        description={errorMessage}
        buttonTitle='Got it'
        onActionClick={() => setErrorMessage(null)}
      />
    </UserMessage>
  );
  return (
    <div className={componentStyles.WRAPPER}>
      {(isInvoicePreviewShown || contextData.isInvoiceCreated) && (
        <UserMessage
          mainWrapperClassName={STYLES.INVOICE_CREATION.USER_MESSAGE.MAIN_WRAPPER}
          contentWrapperClassName={
            shouldShowCopy
              ? STYLES.INVOICE_CREATION.USER_MESSAGE.sideWindowDone
              : STYLES.INVOICE_CREATION.USER_MESSAGE.sideWindow
          }
          onClick={() => setIsInvoicePreviewShown(false)}
          id='invoicePreviewWrapper'
        >
          <SliderUserMessage
            isSubmitAble={isCreationFormValid}
            shouldShowCopy={shouldShowCopy}
            isLoading={isLoading || contextData.isLoading || isUpdatingInvoice}
            handleClick={handleClick}
          />
        </UserMessage>
      )}
      {errorModal}
      {contextData.isLoading || isUpdatingInvoice ? (
        <Spinner spinnerColor={SpinnerColor.Blue} height='2rem' width='2rem' />
      ) : (
        <div className={componentStyles.ACTIONS_WRAPPER}>
          <Tooltip id='preview-button-tooltip' />

          <button
            data-tooltip-id={!isPreviewAvailable ? 'preview-button-tooltip' : ''}
            data-tooltip-content='Add a client to preview an invoice'
            data-tooltip-place='top'
            className={`h-[44px] w-[152px] border rounded-[4px] ${isPreviewAvailable ? 'border-action03 hover:bg-bw02' : 'hover:border-bw05 border-bw05'}`}
            id='stickyHeaderPreviewButton'
            disabled={!isPreviewAvailable}
            onClick={handlePreviewClick}
            type='button'
          >
            <div className='flex flex-row items-center justify-center gap-[10px]'>
              <EyeIcon className={`${!isPreviewAvailable && 'text-bw05'} text-action03 hover:text-action04`} />
              <Heading
                title={t(componentTexts.ACTION_PREVIEW)}
                className={`text-action03 hover:text-action04 font-metric text-[14px] ${!isPreviewAvailable && 'text-bw05 hover:text-bw05'}`}
              />
            </div>
          </button>

          <Tooltip id='create-button-tooltip' />

          <button
            data-tooltip-id={!isCreationFormValid || isItemsValid ? 'create-button-tooltip' : ''}
            data-tooltip-content={tipForCreateToolTip()}
            data-tooltip-place='top'
            className={`h-[44px] w-[152px] bg-action03 hover:bg-action04 rounded-[4px] ${!isCreationFormValid && 'bg-bw05 hover:bg-bw05'}`}
            id='stickyHeaderCreateInvoiceButton'
            disabled={!isCreationFormValid}
            onClick={handleCreateInvoiceClick}
            type='button'
          >
            <Heading title={t(componentTexts.ACTION_CREATE)} className='text-bw00 font-metric text-[14px]' />
          </button>
        </div>
      )}
    </div>
  );
};
